import { RefObject, useEffect, useMemo, useState } from 'react';
import { NetworkList } from '../NetworkList';
import CustomGoogleMap from '../GoogleMap';
import styled from 'styled-components';
import { useBookmarkContext } from 'contexts/bookmark';
import Filters from '../filters/Filters';
import { FullWidthSpace } from 'components';
import { Image, InputRef, Result } from 'antd';
import { breakpoint } from 'styled-components-breakpoint';
import Flag from 'assets/images/flag.svg';
import { useTranslation } from 'react-i18next';

const ListItemNMapWrapper = styled.div`
  display: grid;
  ${breakpoint('tablet')`
    grid-template-columns: 425px 2fr;
    grid-column-gap: 24px;
  `}
`;

type BookmarkTabPaneProp = {
  inputEl: RefObject<InputRef>;
  wrapperRef?: RefObject<HTMLDivElement>;
};

const PAGE_SIZE = 5;

export const BookmarkTabPane = ({ inputEl, wrapperRef }: BookmarkTabPaneProp): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string>(inputEl?.current?.input?.value ?? '');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [districts, setDistricts] = useState<string[] | undefined>(undefined);
  const [googleMapCoords, setGoogleMapCoords] = useState<google.maps.LatLngLiteral | undefined>();
  const [page, setPage] = useState<number>(1);
  const [currentItemId, setCurrentItemId] = useState<number | string | undefined>(undefined);
  const { t } = useTranslation();

  const { filteredBookmark, bookmark } = useBookmarkContext();

  const { startIdx, endIdx } = useMemo(() => {
    return {
      startIdx: (page - 1) * PAGE_SIZE,
      endIdx: (page - 1) * PAGE_SIZE + PAGE_SIZE,
    };
  }, [page]);

  const filtered = useMemo(
    () => filteredBookmark(districts, specialties, searchQuery),
    [districts, specialties, searchQuery, filteredBookmark],
  );

  useEffect(() => {
    if (filtered && filtered.length <= (page - 1) * PAGE_SIZE) {
      setPage((prevState) => (prevState - 1 > 0 ? prevState - 1 : 1));
    }
  }, [filtered, page]);

  useEffect(() => {
    wrapperRef?.current?.scrollIntoView();
  }, [page, wrapperRef]);

  useEffect(() => {
    if (filtered.length) {
      const currentItem = filtered[0];
      setCurrentItemId(currentItem.itemId);
      setGoogleMapCoords({
        lat: currentItem.latitude ?? 0,
        lng: currentItem.longitude ?? 0,
      });
    }
  }, [filtered]);

  const handleOnListItemSelected = (rowId: number | string, latitude: number, longitude: number) => {
    setCurrentItemId(rowId);
    setGoogleMapCoords({ lat: latitude, lng: longitude });
  };

  return (
    <FullWidthSpace direction="vertical" size="middle">
      <Filters
        specialtyValue={specialties}
        handleOnSpecialtyChange={setSpecialties}
        districtValue={districts}
        handleOnDistrictChange={setDistricts}
        handleOnAreaChange={() => console.log()}
        handleOnSearchQueryChange={setSearchQuery}
        showDistrict={true}
        showSpecialty={true}
        inputEl={inputEl}
      />
      {filtered?.length ? (
        <ListItemNMapWrapper>
          <NetworkList
            setPage={setPage}
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered?.length}
            items={filtered?.slice(startIdx, endIdx) || []}
            handleOnListItemSelected={handleOnListItemSelected}
            currentRowId={currentItemId}
          />
          <div>
            <CustomGoogleMap location={googleMapCoords} data={filtered} handleOnMarkerClick={setCurrentItemId} />
          </div>
        </ListItemNMapWrapper>
      ) : !bookmark?.length ? (
        <Result
          icon={<Image src={Flag} preview={false} />}
          title={t('panelList.emptyBookmarks')}
          subTitle={t('panelList.emptyBookmarksTip')}
        />
      ) : (
        !filtered.length && (
          <Result icon={<></>} title={t('panelList.noResults')} subTitle={t('panelList.tryFiltering')} />
        )
      )}
    </FullWidthSpace>
  );
};
